import { getVersion } from 'src/utils/all';
import logger from 'src/logger';
import {
  GIFLoaderComponentType,
  imageComponentType,
  makeGIFLoaderComponent,
  makeImageComponent,
  makeUPNGLoaderComponent,
  UPNGLoaderComponentType,
} from 'src/sdk-components';
import { TourImage, VSInteractionType } from 'src/sdk-components/VSObject';
import { TransformationMode } from 'src/service/scene/scene-nodes/TransformControls/data';
import { LoadingState, TSceneNode } from 'src/service/scene/types';
import { SDKConfig } from 'src/service/types';
import { MpSdk } from 'src/types';
import { SceneMode } from './types';
import Scene from '../../scene';
import { NodeEventsTypes } from '../../enums';

class SceneImage extends Scene {
  readonly mpSDK: any;

  assets = new Array<[any, any[]]>();

  constructor(
    mpSDK: MpSdk,
    private config: SDKConfig,
    private transformControls: any,
  ) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.mpSDK.Scene.register(imageComponentType, makeImageComponent);
    await this.mpSDK.Scene.register(GIFLoaderComponentType, makeGIFLoaderComponent);
    await this.mpSDK.Scene.register(UPNGLoaderComponentType, makeUPNGLoaderComponent);
  }

  async create(data: TourImage): Promise<TSceneNode | undefined> {
    return new Promise(async (resolve) => {
      try {
        const assetNode = this.getAssetNode(data);
        if (assetNode && assetNode.length > 0) {
          resolve(assetNode);
          return;
        }

        // saving the asset into the array to prevent rendering it few times
        const assetsLength = this.assets.push([data, []]);

        // Add your component to the scene node
        const modelNode: any = await this.scene.createNode();
        const url = this.getUrl(data.details.url);
        let type = imageComponentType;
        if (url.includes('.gif')) {
          type = GIFLoaderComponentType;
        } else if (url.includes('.apng')) {
          type = UPNGLoaderComponentType;
        }

        const component = modelNode.addComponent(type, {
          width: data.width,
          height: data.height,
          url,
          eventsEnabled: true,
          userNavigationEnabled: true,
          enableAnimation: data.enableAnimation,
          enableHoverAnimation: data.enableHoverAnimation,
          animationPulse: data.animationPulse,
          animationUpDown: data.animationUpDown,
          animationFlip: data.animationFlip,
          adminPanelAccess: this.config.adminPanelAccess,
          unfiltered: true,
        });

        component.onEvent = (type: string, eventData: any) => {
          // TODO: fix for public
          // it has to be missed for an admin repo!
          // if (data.actionType === TourImageActionType.None) {
          //   return;
          // }

          if (type === VSInteractionType.CLICK) {
            this.emit(NodeEventsTypes.IMAGE_CLICK, { assetId: data.id, eventData, data });

            if (!this.config.adminPanelAccess) {
              if (data.effectOpacity) {
                modelNode.obj3D.traverse((obj) => {
                  if (!obj.isMesh) return;

                  if (Array.isArray(obj.material)) {
                    for (let i = 0; i < obj.material.length; i += 1) {
                      obj.material[i].transparent = true;
                      obj.material[i].opacity = 0.5;
                    }
                  } else {
                    obj.material.transparent = true;
                    obj.material.opacity = 0.5;
                  }
                });
              } else if (data.effectStopAnimation) {
                component.stopAnimation();
              } else if (data.effectHide) {
                modelNode.stop();
              }
            }
          }
          // changing cursor on VS image hover
          // this.iframeWindow.document.body.style.cursor = eventData.hover
          //   ? 'pointer'
          //   : 'auto';
          if (type === VSInteractionType.HOVER) {
            component.toggleHoverAnimation(eventData.hover);
            this.emit(NodeEventsTypes.IMAGE_HOVER, { assetId: data.id, eventData, data });
          }
        };

        modelNode.obj3D.position.set(data.positionX, data.positionY, data.positionZ);
        modelNode.obj3D.rotation.set(data.rotationX, data.rotationY, data.rotationZ);
        modelNode.obj3D.scale.set(data.scaleX, data.scaleY, 0.00001);

        modelNode.start();

        component.outputs.onPropertyChanged('loadingState', async (state: any) => {
          if (state === LoadingState.Loaded) {
            this.assets[assetsLength - 1] = [data, [modelNode, component]];

            resolve([modelNode, component]);
          } else if (state === LoadingState.Error) {
            resolve([] as any);
          }
        });
      } catch (error) {
        logger.error(`Failed to init image ${data.details.title}`, error);
        resolve([] as any);
      }
    });
  }

  remove(data: TourImage) {
    const node = this.getAssetNode(data);
    if (node) {
      const [modelNode] = node;
      if (!modelNode) return;
      modelNode.stop();

      // cleaning up the assets array
      this.assets = this.filterAssetOut(data);
    }
  }

  removeById(id: number) {
    const asset = this.assets.find(([a]) => a.id === id);
    const node = asset?.[1];
    if (node) {
      const [modelNode] = node;
      if (!modelNode) return;
      modelNode.stop();

      // cleaning up the assets array
      this.assets = this.assets.filter(([a]) => a.id !== id);
    }
  }

  async changeMode(
    mode: SceneMode,
    node: TSceneNode,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE,
  ) {
    if (!node) return;

    const [modelNode, component] = node;
    this.setNodeEditView(mode === SceneMode.Edit, component);

    if (mode === SceneMode.Edit) {
      // Make the transform control visible so that the user can manipulate the control selection.
      this.transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      this.transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      this.transformControls.control.inputs.mode = transformationMode;

      // stopping the animation to let user change parameters
      component.stopAnimation();
    } else {
      this.transformControls.control.outputs.objectRoot.visible = false;
      this.transformControls.control.inputs.selection = null;
      this.transformControls.control.inputs.mode = transformationMode;
      component.playAnimation();
    }
  }

  private getUrl = (url: string, updatedAt?: Date) => {
    if (url.includes('http')) {
      return url;
    }
    return `${this.config.appAws}${url}${getVersion(updatedAt ?? new Date())}`;
  };

  setNodeEditView = (state = false, component: any) => {
    /** set state to true to render active component as a wireframe (green color for GLTF models) */

    if (!component) return;
    if (component.outputs.loadingState !== LoadingState.Loaded) return;

    component.outputs.objectRoot?.traverse((obj: THREE.Object3D) => {
      if (obj.type !== 'Mesh') return;
      // check if this is a single material or an array first, depends on the obj
      const mesh = obj as THREE.Mesh;

      const addWireframe = (material: any) => {
        material.transparent = true;
        material.opacity = 0.8;
      };
      const removeWireframe = (material: any) => {
        material.transparent = true;
        material.opacity = 1;
      };

      const wireframeFunction = state ? addWireframe : removeWireframe;

      if (Array.isArray(mesh.material)) {
        for (let i = 0; i < mesh.material.length; i++) {
          wireframeFunction(mesh.material[i]);
        }
      } else {
        wireframeFunction(mesh.material);
      }
    });
  };

  getAssetNode = (asset: any): TSceneNode | null => {
    let node: any = null;
    this.assets.forEach(([a, n]) => {
      if ((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)) {
        node = n;
      }
    });
    return node;
  };

  filterAssetOut = (asset: any): Array<[any, any[]]> => {
    return this.assets.filter(
      ([a]) => !((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)),
    );
  };
}

export default SceneImage;
